import React from 'react';
import { useSelector } from 'react-redux';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Link,
	Typography,
	Theme,
	Skeleton,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { RiShareBoxLine } from 'react-icons/ri';
import { RENTLE_SHOP_TERMS_URL } from 'common/constants/urls';
import { Trans, useTranslation } from 'services/localization';
import PaymentMethodSelect from 'components/PaymentMethodSelect';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { CurrencyObject } from 'common/types';
import { getPricingString } from 'common/utils/common';
import { sumBy } from 'lodash';
import useShopFormat from 'hooks/useShopFormat';
import moment from 'moment-timezone';

export interface ContentString {
	title: string;
	content: string;
	link?: string;
}
export interface LineItem {
	label: string;
	amount: number;
	loading?: boolean;
}
interface Props {
	header?: string;
	cardContent: (ContentString | null)[];
	lineItems: LineItem[];
	disclaimer?: string;
	cta: {
		label: string;
		loading: boolean;
		onClick: () => void;
	};
	isFree?: boolean;
	disabled?: boolean;
	chargeVat: boolean;
	currency: CurrencyObject;
	nextBillingDate?: moment.Moment;
	styling?: 'inline' | 'card';
}
const PurchaseConfirmationCard = (props: Props) => {
	const {
		header,
		cardContent,
		cta,
		disclaimer,
		isFree,
		disabled,
		lineItems,
		currency,
		chargeVat,
		nextBillingDate,
		styling,
	} = props;
	const classes = useStyles();
	const { localFormat } = useShopFormat();

	const { t } = useTranslation();

	const hasPaymentMethod = useSelector(ShopSelectors.shopHasPrimaryPaymentMethod);

	const { subtotal, tax, total, overflow, loading } = (() => {
		const rawSubtotal = sumBy(lineItems, (item) => {
			if (item.loading) return 0;
			return item.amount;
		});
		const overflow = rawSubtotal < 0 ? rawSubtotal * -1 : 0;
		const subtotal = Math.max(rawSubtotal, 0);

		const tax = chargeVat ? Math.floor(subtotal * 0.24) : 0;

		const total = subtotal + tax;

		return {
			subtotal,
			tax,
			total,
			overflow,
			loading: lineItems.some((item) => item.loading),
		};
	})();

	const renderRow = ({
		label,
		value,
		bold,
		loading,
		hidden,
	}: {
		label: string;
		value: string;
		bold?: boolean;
		loading?: boolean;
		hidden?: boolean;
	}) => {
		if (hidden) return null;
		return (
			<Box key={label}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						mt: 1,
					}}
				>
					<Typography
						color="secondary"
						sx={{
							fontWeight: bold ? 500 : 'auto',
						}}
					>
						{label}
					</Typography>
					<Typography
						align="right"
						color="secondary"
						sx={{
							fontWeight: bold ? 500 : 'auto',
						}}
					>
						{loading ? <Skeleton width={80} /> : value}
					</Typography>
				</Box>
			</Box>
		);
	};

	return (
		<Box className={styling === 'inline' ? classes.cardInline : classes.card}>
			{!!header && (
				<Typography className={classes.cardHeader} sx={{ mb: 4 }}>
					{header}
				</Typography>
			)}

			{cardContent?.map((content) => {
				return (
					<Box key={content?.title} className={classes.cardRow}>
						<Typography color="secondary">{content?.title}</Typography>
						<Box className={classes.featureLink}>
							<Typography align="right">{content?.content}</Typography>
							{content?.link && (
								<Box ml={1}>
									<a href={content?.link} target="_blank" rel="noopener noreferrer">
										<RiShareBoxLine size={16} />
									</a>
								</Box>
							)}
						</Box>
					</Box>
				);
			})}

			{!!disclaimer && (
				<Box mt={2}>
					<Typography variant="caption">{disclaimer}</Typography>
				</Box>
			)}
			{!isFree && (
				<>
					<Box my={4}>
						<Divider />
					</Box>
					<Typography className={classes.sectionTitle} variant="body2" color="secondary">
						{t('common:paymentMethods.paymentMethod', 'Payment method')}
					</Typography>
					<Box mt={3} />
					<PaymentMethodSelect />
					<Box my={4}>
						<Divider />
					</Box>
					<Box className={classes.cardRow}>
						<Typography className={classes.sectionTitle} variant="body2" color="secondary">
							{t('account.stores.billing', 'billing')}
						</Typography>
					</Box>
					{lineItems.map((item) =>
						renderRow({
							label: item.label,
							value: getPricingString(item.amount, currency),
							loading: item.loading,
							hidden: !item.loading && !item.amount,
						}),
					)}
					<Box my={1.5}>
						<Divider />
					</Box>
					{tax > 0 ? (
						<>
							{renderRow({
								label: t('common:payment.subtotal', 'Subtotal'),
								value: getPricingString(subtotal, currency),
								loading,
							})}
							{renderRow({
								label: t('common:taxation.tax', 'Tax'),
								value: getPricingString(tax, currency),
								loading,
							})}
							<Box my={1.5}>
								<Divider />
							</Box>
						</>
					) : null}
					{renderRow({
						label: t('common:payment.chargedNow', 'Charged now'),
						value: getPricingString(total, currency),
						bold: true,
						loading,
					})}
					{!loading && overflow > 0
						? renderRow({
								label: t('plans.billingBreakdown.creditsAdded', 'Credits added'),
								value: getPricingString(overflow, currency),
						  })
						: null}
					{!!nextBillingDate
						? renderRow({
								label: t('account.stores.nextBillingDate', 'Next billing date'),
								value: localFormat(moment(nextBillingDate), 'MMMM Do YYYY'),
						  })
						: null}
				</>
			)}
			<Box my={4}>
				<Button
					fullWidth
					disabled={cta.loading || (!hasPaymentMethod && !isFree) || disabled || loading}
					variant="contained"
					color="primary"
					onClick={cta.onClick}
				>
					{cta.loading ? <CircularProgress size={20} color="inherit" /> : cta.label}
				</Button>
			</Box>
			{!isFree && (
				<Box className={classes.cardRow}>
					<Typography className={classes.termsText} variant="body2" color="secondary">
						<Trans
							i18nKey="payment.termsAgreement"
							defaults="By starting a plan, you allow Rentle to charge your card for this and future payments according to the <terms>Terms & Conditions</terms>"
							components={{
								terms: (
									<Link
										className={classes.termsLink}
										href={RENTLE_SHOP_TERMS_URL}
										target="_blank"
										rel="noopener noreferrer"
									>
										{'Terms & Conditions'}
									</Link>
								),
							}}
						/>
					</Typography>
				</Box>
			)}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		card: {
			background: theme.palette.background.paper,
			border: `1px solid ${theme.palette.border.paper}`,
			borderRadius: '4px',
			padding: theme.spacing(4),

			maxWidth: '100%',
		},
		cardInline: {},
		cardHeader: {
			textAlign: 'center',
			fontSize: '2.3rem',
			fontWeight: 500,
		},
		cardRow: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginTop: theme.spacing(2),
		},
		featureLink: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		sectionTitle: {
			textTransform: 'uppercase',
			color: theme.palette.text.disabled,
			fontWeight: 600,
		},
		termsLink: {
			textDecoration: 'underline',
			color: theme.palette.primary.main,
		},
		termsText: {
			fontSize: '1.2rem',
			lineHeight: '1.7rem',
			color: theme.palette.colors.tundra.main,
		},
	}),
);

export default PurchaseConfirmationCard;
